import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import BemClass from '@upsales/components/Utils/bemClass';
import {
	ButtonSelect,
	Checkbox,
	Label,
	DateInput,
	Text,
	TimeInput,
	DropDownMenu,
	Table,
	TableRow,
	TableColumn
} from '@upsales/components';
import moment from 'moment';
import { isRememberPostponeEnabled, setRememberPostponeEnabled } from 'App/babel/enum/activityOutcome';
import { savePostponePreference } from '../postponePreferenceStorage';

import './index.scss';

class OutcomePostponeDate extends PureComponent {
	constructor(props) {
		super(props);

		const t = Tools.$translate;
		this.lang = {
			postponeDate: t('activity.outcome.postponeDate'),
			customDate: t('activity.outcome.customDate'),
			remember: t('activity.outcome.rememberPostpone'),
			time: t('default.time'),
			today: t('activity.today')
		};

		this.state = { remember: isRememberPostponeEnabled() };
	}

	toggleRemember = () => {
		const remember = !this.state.remember;
		const { presetValue, date, showTime } = this.props;
		// Ticking the box captures what is selected right now, so the rep does not have to
		// pick the preset a second time for it to be remembered.
		setRememberPostponeEnabled(remember, presetValue);
		// "Later today" is the one preset whose point is the time, so capture how far ahead it
		// is too - otherwise the next call falls back to the activity's own clock.
		if (remember && presetValue === 'currentDay' && showTime && date) {
			savePostponePreference(presetValue, date);
		}
		this.setState({ remember });
	};

	onDateChange = ({ target }) => {
		const { date } = this.props;

		if (!moment(date).isSame(target.value)) {
			this.props.onDateChange(target.value);
		}
	};

	onTimeChange = ({ target }) => {
		this.props.onTimeChange(target.value);
	};

	savePreference = (preset, selectedTime = null) => {
		savePostponePreference(preset, selectedTime);
	};

	renderRows(closeMenu) {
		const now = moment();
		const endDay = moment().endOf('day');
		const hoursLeft = endDay.diff(now, 'hours') || 0;
		let hours = now.set({
			minutes: 0,
			seconds: 0
		});

		if (hoursLeft <= 0) return null;

		return Array.from(new Array(hoursLeft)).map(() => {
			hours = hours.add(1, 'hour');
			const timeFormat = hours.format('LT');
			const handleClick = ({ currentTarget }) => {
				const selectedTime = new Date(currentTarget.dataset.datetime);
				this.props.onTimeChange(selectedTime);
				// Save the "later today" preference with the selected time
				this.savePreference('currentDay', selectedTime);
				closeMenu();
			};

			return (
				<TableRow key={hours.valueOf()} onClick={handleClick} data-datetime={hours.toISOString()}>
					<TableColumn>
						{timeFormat} {this.lang.today}
					</TableColumn>
				</TableRow>
			);
		});
	}

	render() {
		const classNames = new BemClass('OutcomePostponeDate');
		const { presets, presetValue, date, disabled, showTime, dateAnchor, dateScrollContainer } = this.props;
		const suggestedTime = moment()
			.add(30 - (moment().minute() % 30), 'minutes')
			.format('HH:mm');
		return (
			<div className={classNames.b()}>
				<div className={classNames.elem('postpone').b()}>
					<div className={classNames.elem('postponeHeader').b()}>
						<Label>{this.lang.postponeDate}</Label>
						<label className={classNames.elem('remember').b()}>
							<Checkbox size="sm" checked={this.state.remember} onChange={this.toggleRemember} />
							<Text size="sm" color="grey-11">
								{this.lang.remember}
							</Text>
						</label>
					</div>
					<DropDownMenu
						align="left"
						verticalAlign="top"
						anchor={dateAnchor}
						renderTrigger={(isExpanded, setExpanded) => {
							const handPresetChange = preset => {
								if (preset === 'currentDay') {
									setExpanded(new Event('click'));
								} else {
									// Save the preset preference (for non-currentDay presets)
									this.savePreference(preset);
								}
								this.props.onPresetChange(preset);
							};

							const handleButtonSelectClick = e => {
								// ButtonSelect does not fire onChange when re-clicking the already selected value.
								// We need to detect clicks on the "currentDay" button manually so the
								// time-picker dropdown still opens when "Later today" is already selected.
								if (presetValue === 'currentDay') {
									const btn = e.target.closest?.('.Button');
									if (btn && !isExpanded) {
										setExpanded(e);
									}
								}
							};

							return (
								<ButtonSelect
									size="md"
									options={presets}
									value={presetValue}
									onChange={handPresetChange}
									onClick={handleButtonSelectClick}
								/>
							);
						}}
					>
						{closeMenu => (
							<div className={classNames.elem('postponeLaterToday').b()}>
								<Table>{this.renderRows(closeMenu)}</Table>
							</div>
						)}
					</DropDownMenu>
				</div>
				<div>
					<Label>{this.lang.customDate}</Label>
					<div className={classNames.elem('date-time-picker').b()}>
						<DateInput
							disabled={disabled}
							value={date}
							closeOnSelect
							showWeekNumbers={true}
							className={classNames.elem('date-picker').b()}
							onChange={this.onDateChange}
							calendarSize={6}
							anchor={dateAnchor}
							scrollContainer={dateScrollContainer}
						/>
						<TimeInput
							asDate
							disabled={disabled}
							placeholder={this.lang.time}
							value={showTime ? date : ''}
							className={classNames.elem('time-picker').b()}
							onChange={this.onTimeChange}
							anchor={dateAnchor}
							scrollContainer={dateScrollContainer}
							defaultSelected={suggestedTime}
							min={this.props.minTime}
						/>
					</div>
				</div>
			</div>
		);
	}
}

OutcomePostponeDate.defaultProps = {
	onPresetChange: () => {},
	onDateChange: () => {},
	onTimeChange: () => {},
	presets: []
};

OutcomePostponeDate.propTypes = {
	presets: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string,
			value: PropTypes.string
		})
	),
	presetValue: PropTypes.string,
	onPresetChange: PropTypes.func,
	date: PropTypes.instanceOf(Date),
	showTime: PropTypes.bool,
	onDateChange: PropTypes.func,
	onTimeChange: PropTypes.func,
	disabled: PropTypes.bool,
	dateAnchor: PropTypes.oneOfType([PropTypes.element, PropTypes.object]),
	dateScrollContainer: PropTypes.oneOfType([PropTypes.element, PropTypes.object]),
	minTime: PropTypes.object
};

export default OutcomePostponeDate;
